import React, { useState } from "react";
import { Grid, Paper, Stack, Typography, Chip, Box, Button, CircularProgress } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import MicOffIcon from "@mui/icons-material/MicOff";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "../../helpers/request";
import styled from "@emotion/styled";

// Map issue types to human-readable labels and colors
const ISSUE_TYPE_MAP = {
  very_low_quality: { label: "Very Low Quality", color: "#ef4444", icon: SignalCellularAltIcon },
  low_quality: { label: "Low Quality", color: "#f59e0b", icon: SignalCellularAltIcon },
  high_packet_loss: { label: "High Packet Loss", color: "#ef4444", icon: WarningAmberIcon },
  high_latency: { label: "High Latency", color: "#f59e0b", icon: WarningAmberIcon },
  high_jitter: { label: "High Jitter", color: "#f59e0b", icon: WarningAmberIcon },
  low_bandwidth: { label: "Low Bandwidth", color: "#f59e0b", icon: WarningAmberIcon },
  reconnecting: { label: "Reconnecting", color: "#ef4444", icon: ErrorOutlineIcon },
  signaling_error: { label: "Signaling Error", color: "#ef4444", icon: ErrorOutlineIcon },
  signaling_busy: { label: "Server Busy", color: "#f59e0b", icon: WarningAmberIcon },
  media_connection_failed: { label: "Media Failed", color: "#ef4444", icon: ErrorOutlineIcon },
  track_publication_failed: { label: "Track Failed", color: "#ef4444", icon: ErrorOutlineIcon },
  audio_denied: { label: "Audio Denied", color: "#ef4444", icon: MicOffIcon },
  video_denied: { label: "Video Denied", color: "#f59e0b", icon: VideocamOffIcon },
};

// Styled components matching client design
const IssueContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  backgroundColor: '#fff',
  margin: theme.spacing(1, 0),
}));

const IssueIndicator = styled(Box)(({ indicatorcolor }) => ({
  width: '8px',
  backgroundColor: indicatorcolor || '#ef4444',
  borderRadius: '2px 0 0 2px',
  flexShrink: 0,
}));

const IssueContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2, 3),
}));

const IssueRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1),
}));

const IssueLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: '#374151',
  minWidth: '120px',
  fontSize: '0.875rem',
}));

const IssueValue = styled(Box)(() => ({
  flex: 1,
}));

const IssueTypeBadge = styled(Chip)(({ badgecolor }) => ({
  backgroundColor: badgecolor || '#f3f4f6',
  color: badgecolor ? '#fff' : '#374151',
  fontWeight: 500,
  height: '24px',
  fontSize: '0.75rem',
  '& .MuiChip-label': {
    padding: '0 10px',
  },
}));

// Parse network_issue string into individual issue types
const parseNetworkIssues = (networkIssue) => {
  if (!networkIssue) return [];
  return networkIssue.split(',').map(issue => issue.trim()).filter(Boolean);
};

// Get issue display props
const getIssueDisplayProps = (issueType) => {
  const config = ISSUE_TYPE_MAP[issueType];
  if (config) {
    return {
      label: config.label,
      color: config.color,
    };
  }
  // Fallback for unknown issue types
  return {
    label: issueType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    color: '#ef4444',
  };
};

const InteractionIssue = (props) => {
  const { row, onIssueResolved } = props;
  const [isResolved, setIsResolved] = useState(row.issue_resolved || false);
  const [loading, setLoading] = useState(false);
  
  // Parse network issues into an array
  const issueTypes = parseNetworkIssues(row.network_issue);
  
  // Determine if we have any issues to display
  const hasAudioPermissionIssue = row.audio_permission === false;
  const hasVideoPermissionIssue = row.video_permission === false;
  const hasNetworkIssues = issueTypes.length > 0 || row.issue_reason;
  const hasAnyIssue = hasAudioPermissionIssue || hasVideoPermissionIssue || hasNetworkIssues;

  // Get primary indicator color (use first issue's color)
  const getPrimaryColor = () => {
    // if (hasAudioPermissionIssue) return '#ef4444';
    // if (issueTypes.length > 0) {
    //   const firstIssue = getIssueDisplayProps(issueTypes[0]);
    //   return firstIssue.color;
    // }
    // if (hasVideoPermissionIssue) return '#f59e0b';
    return '#ef4444';
  };

  const handleMarkResolved = async () => {
    if (loading || isResolved) return;
    
    setLoading(true);
    try {
      await axios.put('/internal/update-issue', {
        sid: row.participantid,
        issue_resolved: true
      });
      setIsResolved(true);
      if (onIssueResolved) {
        onIssueResolved(row.participantid);
      }
    } catch (error) {
      console.error('Error marking issue as resolved:', error);
      alert('Failed to mark issue as resolved');
    } finally {
      setLoading(false);
    }
  };

  if (!hasAnyIssue) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No issues detected for this participant.
        </Typography>
      </Box>
    );
  }

  // Get all issue type labels for display
  const allIssueLabels = [];
  if (hasAudioPermissionIssue) allIssueLabels.push({ label: 'Audio Permission Denied', color: '#ef4444' });
  if (hasVideoPermissionIssue) allIssueLabels.push({ label: 'Video Permission Denied', color: '#f59e0b' });
  issueTypes.forEach(type => {
    const props = getIssueDisplayProps(type);
    if (!allIssueLabels.find(l => l.label === props.label)) {
      allIssueLabels.push(props);
    }
  });

  // Build error description
  const buildErrorDescription = () => {
    const descriptions = [];
    
    if (hasAudioPermissionIssue) {
      descriptions.push('The participant did not grant microphone access. They could not speak during the meeting.');
    }
    if (hasVideoPermissionIssue) {
      descriptions.push('The participant did not grant camera access. Their video was not visible.');
    }
    if (row.issue_reason) {
      // Split by semicolon and add each as a separate line
      row.issue_reason.split(';').forEach(reason => {
        const trimmed = reason.trim();
        if (trimmed) descriptions.push(trimmed);
      });
    } else if (hasNetworkIssues && issueTypes.length > 0) {
      descriptions.push(`Participant experienced ${issueTypes.map(t => getIssueDisplayProps(t).label.toLowerCase()).join(' and ')} during the meeting, causing a/v quality degradation and intermittent connectivity issues.`);
    }
    
    return descriptions;
  };

  const errorDescriptions = buildErrorDescription();

  return (
    <Box sx={{ py: 1, px: 2, ml: 6 }}>
      <IssueContainer>
        <IssueIndicator indicatorcolor={getPrimaryColor()} />
        <IssueContent>
          {/* Issue Type Row */}
          <IssueRow>
            <IssueLabel>Error type:</IssueLabel>
            <IssueValue>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#ef4444',
                  fontWeight: 500,
                }}
              >
                {allIssueLabels.map(issue => issue.label).join(', ') || 'Network Issue'}
              </Typography>
            </IssueValue>
          </IssueRow>

          {/* Error Description Row */}
          {errorDescriptions.length > 0 && (
            <IssueRow sx={{ mt: 1.5 }}>
              <IssueLabel>Error description:</IssueLabel>
              <IssueValue>
                {errorDescriptions.map((desc, index) => (
                  <Typography 
                    key={index} 
                    variant="body2" 
                    sx={{ 
                      color: '#4b5563',
                      mb: index < errorDescriptions.length - 1 ? 0.5 : 0,
                      lineHeight: 1.5,
                    }}
                  >
                    {errorDescriptions.length > 1 ? `• ${desc}` : desc}
                  </Typography>
                ))}
              </IssueValue>
            </IssueRow>
          )}
        </IssueContent>
      </IssueContainer>

      {/* Status and Action Button - Outside the bordered section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, ml: 2 }}>
        {!isResolved ? (
          <Button
            size="small"
            variant="contained"
            onClick={handleMarkResolved}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
            sx={{
              backgroundColor: '#22c55e',
              borderRadius: '20px',
              textTransform: 'none',
              fontWeight: 500,
              px: 2,
              '&:hover': {
                backgroundColor: '#16a34a',
              },
            }}
          >
            {loading ? 'Updating...' : 'Mark as Resolved'}
          </Button>
        ) : (
          <Chip
            icon={<CheckCircleIcon />}
            label="Issue Resolved"
            sx={{
              backgroundColor: '#22c55e',
              color: '#fff',
              fontWeight: 500,
              '& .MuiChip-icon': {
                color: '#fff',
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default InteractionIssue;

